import { useEffect, useState } from 'react';
import {
  useStore, boot, openProject, newProject, undo, redo, deleteSelected, duplicateSelected, nudge, selectAll, clearSelection,
  resolveConflict, restoreRecovery, addFiles, openPanel, setFocus, emailOn, openEmailFlow, toast
} from './store/store';
import { useRoute, navigate } from './router';
import { Header } from './ui/Header';
import { Toasts, Wait, Dialog } from './ui/primitives';
import { Workspace } from './workspace/Workspace';
import { StartFreshPrompt } from './workspace/StartFresh';
import { PreviewMode } from './screens/Preview';
import { Mine } from './screens/Mine';
import { Admin } from './screens/Admin';
import { OrderedView } from './screens/Ordered';
import { EmailFlow, LostDialog } from './ui/Email';
import { Icon } from './ui/icons';

export function App() {
  const cfg = useStore(s => s.config);
  const view = useStore(s => s.view);
  const pid = useStore(s => s.projectId);
  const preview = useStore(s => s.preview);
  const conflict = useStore(s => s.conflict);
  const recovery = useStore(s => s.recovery);
  const route = useRoute();
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { boot().catch(e => setErr(e.message)); }, []);

  // route → screen
  useEffect(() => {
    if (!cfg) return;
    if (route.name === 'project') {
      // a gangsheet that won't open here (not in this browser, or its email sign-in ended): say so, with
      // Find My Gangsheets (0.3e Stage B), over the start chooser
      if (route.id !== pid) openProject(route.id).catch((e: { status?: number; message?: string }) => {
        navigate('/', true);
        const gone = e?.status === 404 || e?.status === 401;
        useStore.setState({ view: 'start', startOpen: true, lost: gone ? 'gone' : null });
        if (!gone) toast(e?.message || 'We couldn’t open that gangsheet.', { bad: true });
      });
      else if (view === 'mine' || view === 'admin') { const s = useStore.getState(); useStore.setState({ view: s.meta.orderedAt ? 'ordered' : s.doc.sheets.length ? 'review' : 'designs' }); }
    } else if (route.name === 'my') useStore.setState({ view: 'mine' });
    else if (route.name === 'find') {
      // the link in the confirmation email: My gang sheets with Find My Gangsheets open
      navigate('/my', true);
      useStore.setState({ view: 'mine' });
      if (emailOn()) openEmailFlow('find');
    }
    else if (route.name === 'admin') useStore.setState({ view: 'admin' });
    else if (route.name === 'start' && (view === 'mine' || view === 'admin' || view === 'ordered' || pid)) {
      if (pid) newProject(); else useStore.setState({ view: 'start' });
    }
  }, [route, cfg]);

  // shortcuts (workspace)
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      const s = useStore.getState();
      const mod = e.ctrlKey || e.metaKey;
      if (!inWorkspace(s.view) || s.busy || s.reviewOpen || s.preview || document.querySelector('.scrim')) return;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); return; }
      if (!mod && (e.key === 'f' || e.key === 'F')) { setFocus(!s.focus); return; }
      if (e.key === 'Escape' && !s.selection.length && s.focus) { setFocus(false); return; }
      if (!s.doc.sheets.length) return;
      if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); void duplicateSelected(1); return; }
      if (mod && e.key.toLowerCase() === 'a') { e.preventDefault(); selectAll(); return; }
      if (mod) return;
      if (e.key === 'Delete' || e.key === 'Backspace') { if (s.selection.length) { e.preventDefault(); deleteSelected(); } return; }
      if (e.key === 'Escape') { if (s.selection.length) clearSelection(); else if (s.panel && window.innerWidth < 1100) openPanel(null); return; }
      if (e.key === 'v' || e.key === 'V') { useStore.setState({ panMode: false }); return; }
      if (e.key === 'h' || e.key === 'H') { useStore.setState({ panMode: true }); return; }
      const step = e.shiftKey ? 10 : 1;
      if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(-step, 0); }
      if (e.key === 'ArrowRight') { e.preventDefault(); nudge(step, 0); }
      if (e.key === 'ArrowUp') { e.preventDefault(); nudge(0, -step); }
      if (e.key === 'ArrowDown') { e.preventDefault(); nudge(0, step); }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, []);

  // files dropped anywhere while working go to the design list
  useEffect(() => {
    const over = (e: DragEvent) => { if (e.dataTransfer?.types.includes('Files')) e.preventDefault(); };
    const drop = (e: DragEvent) => {
      if (!e.dataTransfer?.files.length) return;
      e.preventDefault();
      if (inWorkspace(useStore.getState().view)) void addFiles(e.dataTransfer.files);
    };
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => { window.removeEventListener('dragover', over); window.removeEventListener('drop', drop); };
  }, []);

  if (err) return (
    <div className="app"><Header /><main className="boot"><div className="boot-card">
      <h1 className="h2">We can’t open the builder right now</h1><p className="muted" style={{ marginTop: 8 }}>{err}</p>
      <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => location.reload()}>Try again</button>
    </div></main></div>
  );
  if (!cfg) return <div className="app"><Header /><main className="boot" aria-busy="true"><span className="spin" /></main></div>;

  return (
    <div className={'app' + (inWorkspace(view) ? ' app-ws' : '')}>
      {!inWorkspace(view) && <Header />}
      {inWorkspace(view) && <Workspace />}
      {view === 'mine' && <Mine />}
      {view === 'admin' && <Admin />}
      {view === 'ordered' && <OrderedView />}
      {preview && <PreviewMode />}
      <StartFreshPrompt />
      {conflict && (
        <Dialog title="Changed somewhere else">
          <span className="kicker">Heads up</span>
          <h2>This gangsheet was changed in another tab</h2>
          <p className="muted">Keep the version you’re looking at, or load the other one.</p>
          <div className="dialog-actions">
            <button className="btn btn-line btn-sm" onClick={() => resolveConflict('theirs')}>Load the other version</button>
            <button className="btn btn-primary btn-sm" onClick={() => resolveConflict('mine')}>Keep mine</button>
          </div>
        </Dialog>
      )}
      {recovery && (
        <Dialog title="Restore unsaved changes" onClose={() => restoreRecovery(false)}>
          <span className="kicker">Welcome back</span>
          <h2>We kept your unsaved changes</h2>
          <p className="muted">From {new Date(recovery.at).toLocaleString('en-NZ')}. They didn’t reach our server before the page closed.</p>
          <div className="dialog-actions">
            <button className="btn btn-line btn-sm" onClick={() => restoreRecovery(false)}>No thanks</button>
            <button className="btn btn-primary btn-sm" onClick={() => restoreRecovery(true)}><Icon name="undo" />Restore them</button>
          </div>
        </Dialog>
      )}
      <EmailFlow />
      <LostDialog />
      <Wait />
      {!inWorkspace(view) && <Toasts />}
    </div>
  );
}

/** start, designs, review and editor are all the one workspace now (the names stay for links and saved state) */
function inWorkspace(v: string) { return v === 'start' || v === 'designs' || v === 'review' || v === 'editor'; }
